import "./SectionScoreCard.css";

import { round } from "lodash-es";
import { type FC, useId } from "react";
import { twMerge } from "tailwind-merge";

import { calculateRubricScore } from "components/materials/section/assessment/hooks";
import { SECTION_SETTINGS_GRADE_TAB_INDEX } from "components/modals/SectionSettingsModal";
import { SectionScoreDisplayFormat, SectionType } from "components/server-types";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { Tooltip } from "mds/components/Tooltip";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { CircleInfoIcon, GearSettingsIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppDispatch, useAppSelector } from "store/index";
import {
  selectAverageSectionScoreForGradeCategory,
  selectCurrentOrg,
  selectCurrentSection,
  selectCurrentUserId,
  selectSectionUserIdByUserId,
  selectUserById,
} from "store/selectors";
import type { GradeCategory } from "store/selectors/types";
import { viewStateActions } from "store/slices/view";
import { getUserFullName } from "utils/user-utils";

type SectionScoreCardSectionProps = {
  className?: string;
  title: string;
  maxValue: number;
  currentValue?: number;
  isSectionScore?: boolean;
  userId?: string | null;
};

const SectionScoreCardSection: FC<SectionScoreCardSectionProps> = ({
  className,
  title,
  maxValue,
  currentValue,
  isSectionScore,
  userId,
}) => {
  const currentOrg = useAppSelector(selectCurrentOrg);
  const { canAuthorSection } = useCapabilities();
  const id = useId();

  const sectionScoreDisplayFormat =
    currentOrg?.section_score_display_format || SectionScoreDisplayFormat.Percent;
  const shouldShowPercent =
    !isSectionScore || sectionScoreDisplayFormat === SectionScoreDisplayFormat.Percent;

  return (
    <div className={twMerge("flex min-w-1 shrink flex-col gap-2", className)}>
      <div className="flex min-w-1 shrink flex-row items-center justify-between gap-1">
        <label className="min-w-1 shrink truncate text-sm font-normal" htmlFor={id}>
          {title}
        </label>{" "}
        {currentValue === undefined ? (
          <div className="shrink-0 text-sm font-semibold text-blue">
            {maxValue}
            {shouldShowPercent ? "%" : undefined}
          </div>
        ) : (
          <Tooltip
            text={t(
              `assessment_dashboard.tooltip.${canAuthorSection && !userId ? "instructor" : "student"}.${isSectionScore ? sectionScoreDisplayFormat : "category"}`,
            )}
          >
            <div className="flex shrink-0 items-baseline gap-1 whitespace-nowrap">
              <span className="text-sm font-semibold text-blue">{currentValue}</span>
              <span className="text-xs font-normal text-black-tint-40">
                {" "}
                / {maxValue}
                {shouldShowPercent ? "%" : undefined}
              </span>
            </div>
          </Tooltip>
        )}
      </div>
      <meter className="h-1 w-full" id={id} max={maxValue} value={currentValue} />
    </div>
  );
};

const getTotalValue = (
  section: SectionType | null,
  prepworkAvgGrade: number | undefined,
  activitiesAvgGrade: number | undefined,
  assignmentsAvgGrade: number | undefined,
) => {
  if (!section) return undefined;
  // Can't calculate total if any of the *relevant* categories are undefined...
  // but if a category has a percentage of 0, then it's not relevant.
  if (
    (prepworkAvgGrade === undefined && section.section_score_prepwork_percentage > 0) ||
    (activitiesAvgGrade === undefined && section.section_score_activities_percentage > 0) ||
    (assignmentsAvgGrade === undefined && section.section_score_assignments_percentage > 0)
  ) {
    return undefined;
  }

  const prepworkValue = (prepworkAvgGrade ?? 0) * section.section_score_prepwork_percentage;
  const activitiesValue = (activitiesAvgGrade ?? 0) * section.section_score_activities_percentage;
  const assignmentsValue =
    (assignmentsAvgGrade ?? 0) * section.section_score_assignments_percentage;

  return round(prepworkValue, 1) + round(activitiesValue, 1) + round(assignmentsValue, 1);
};

const getSingleTotalBucket = (section: SectionType | null): GradeCategory | undefined => {
  if (!section) return undefined;
  if (section.section_score_prepwork_percentage === 100) {
    return "prepwork";
  }
  if (section.section_score_activities_percentage === 100) {
    return "activities";
  }
  if (section.section_score_assignments_percentage === 100) {
    return "assignments";
  }
  return undefined;
};

type SectionScoreCardProps = {
  /**
   * If `userId` is specified, this component will show the grade for the given user ID;
   * otherwise, it will show the average grade for the entire class.
   * Only instructors can specify this prop; for students, it is ignored.
   */
  userId?: string | null;
};

export const SectionScoreCard: FC<SectionScoreCardProps> = ({ userId: userIdProp = null }) => {
  const dispatch = useAppDispatch();
  const section = useAppSelector(selectCurrentSection);
  const currentUserId = useAppSelector(selectCurrentUserId);
  const currentOrg = useAppSelector(selectCurrentOrg);
  const { canAuthorSection } = useCapabilities();

  // If the current user is a student, they are not allowed to view the grade for any other students.
  // Therefore, if the `userId` prop is not provided, we use the current user ID.
  // Instructors are allowed to view the grades for all students, so they can pass a specific `userId`.
  const userId = (canAuthorSection ? userIdProp : currentUserId) || null;
  const user = useAppSelector((s) => selectUserById(s, userId));
  const sectionUserId = useAppSelector((s) => selectSectionUserIdByUserId(s, userId));

  const prepworkAvgGrade = useAppSelector((s) =>
    selectAverageSectionScoreForGradeCategory(s, "prepwork", sectionUserId),
  );
  const activitiesAvgGrade = useAppSelector((s) =>
    selectAverageSectionScoreForGradeCategory(s, "activities", sectionUserId),
  );
  const assignmentsAvgGrade = useAppSelector((s) =>
    selectAverageSectionScoreForGradeCategory(s, "assignments", sectionUserId),
  );
  const totalValue = getTotalValue(
    section,
    prepworkAvgGrade,
    activitiesAvgGrade,
    assignmentsAvgGrade,
  );
  const singleTotalBucket = getSingleTotalBucket(section);
  const isMdOrLarger = useIsMdOrLarger();

  const sectionScoreDisplayFormat = currentOrg?.section_score_display_format;
  const orgRubric = currentOrg?.rubric || [];

  const maxSectionScoreForDisplay =
    sectionScoreDisplayFormat === SectionScoreDisplayFormat.Percent
      ? 100
      : sectionScoreDisplayFormat === SectionScoreDisplayFormat.Scale_4
        ? 4.0
        : 5.0;

  const sectionScoreForDisplay =
    totalValue === undefined
      ? undefined
      : sectionScoreDisplayFormat === SectionScoreDisplayFormat.Percent
        ? totalValue
        : calculateRubricScore(orgRubric, totalValue);

  // We should only show the values if the section scores are published,
  // or if the user can author the section (in which case they can see the unpublished values).
  const shouldShowValues = !!section?.section_score_published_at || canAuthorSection;

  const openGradeSettings = () => {
    dispatch(viewStateActions.setSectionSettingsTabIndex(SECTION_SETTINGS_GRADE_TAB_INDEX));
  };

  return (
    <div className="section-score-card flex w-full flex-col gap-3 rounded-xl bg-blue-tint-95 p-6 pt-5">
      <div className="flex items-center justify-between gap-2">
        <IconText
          className="min-w-1 text-lg font-semibold text-blue"
          iconEnd={
            totalValue === undefined ? (
              <Tooltip text={t("assessment_dashboard.tooltip.section_score")}>
                <CircleInfoIcon />
              </Tooltip>
            ) : undefined
          }
          text={
            <div className="shrink truncate">
              {isMdOrLarger
                ? canAuthorSection
                  ? user
                    ? t("assessment_dashboard.grades.title_name", {
                        name: getUserFullName(user),
                      })
                    : totalValue
                      ? t("assessment_dashboard.grades.title_average")
                      : t("assessment_dashboard.grades.title_calculations")
                  : t("assessment_dashboard.grades.title_own")
                : t("assessment_dashboard.grades.title_short")}
            </div>
          }
        />
        {canAuthorSection && (
          <Button className="shrink-0 border-0" kind="white" size="s" onClick={openGradeSettings}>
            <IconText
              iconStart={<GearSettingsIcon />}
              text={
                isMdOrLarger
                  ? t("assessment_dashboard.grades.settings")
                  : t("assessment_dashboard.grades.settings_short")
              }
            />
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-4">
        <SectionScoreCardSection
          currentValue={
            shouldShowValues && typeof sectionScoreForDisplay === "number"
              ? round(
                  sectionScoreForDisplay,
                  sectionScoreDisplayFormat === SectionScoreDisplayFormat.Percent ? 1 : 2,
                )
              : undefined
          }
          maxValue={maxSectionScoreForDisplay}
          title={
            singleTotalBucket
              ? `${t("assessment_dashboard.grades.total")}: ${t(`assessment_dashboard.grades.${singleTotalBucket}`)}`
              : t("assessment_dashboard.grades.total")
          }
          userId={userId}
          isSectionScore
        />
        {!singleTotalBucket && (
          <div className="flex flex-col gap-4 md:flex-row md:gap-6">
            {(
              [
                ["prepwork", prepworkAvgGrade],
                ["activities", activitiesAvgGrade],
                ["assignments", assignmentsAvgGrade],
              ] as const
            ).map(([category, avgPoints]) => {
              const maxValue = section?.[`section_score_${category}_percentage`] ?? 0;
              return maxValue > 0 ? (
                <div
                  className="min-w-1 shrink"
                  key={category}
                  style={isMdOrLarger ? { width: `${Math.max(maxValue, 25)}%` } : undefined}
                >
                  <SectionScoreCardSection
                    currentValue={
                      shouldShowValues && typeof avgPoints === "number"
                        ? round(avgPoints * maxValue, 1)
                        : undefined
                    }
                    maxValue={maxValue}
                    title={t(`assessment_dashboard.grades.${category}`)}
                    userId={userId}
                  />
                </div>
              ) : null;
            })}
          </div>
        )}
      </div>
    </div>
  );
};
